import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { PHOTOS } from "./assets";

const HERO_VIDEO_WEBM = `${process.env.PUBLIC_URL || ""}/hero.webm`;
const HERO_VIDEO_MP4 = `${process.env.PUBLIC_URL || ""}/hero.mp4`;

const rise = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduceMotion) {
      v.pause();
    } else {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [reduceMotion, videoFailed]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <div className="absolute inset-0">
        {videoFailed ? (
          <motion.img
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            src={PHOTOS.hero}
            alt="Athletes sprinting in training"
            className="w-full h-full object-cover object-[center_30%] grayscale"
            data-testid="hero-fallback-image"
          />
        ) : (
          <motion.div
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full"
          >
            <video
              ref={videoRef}
              autoPlay={!reduceMotion}
              muted
              loop
              playsInline
              preload="auto"
              poster={PHOTOS.hero}
              onError={() => setVideoFailed(true)}
              className="w-full h-full object-cover object-[center_30%] grayscale"
              data-testid="hero-video"
            >
              <source src={HERO_VIDEO_WEBM} type="video/webm" />
              <source src={HERO_VIDEO_MP4} type="video/mp4" />
            </video>
          </motion.div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent" />
      </div>


      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pb-28 md:pb-36 pt-32">
        <motion.p
          variants={rise}
          initial="hidden"
          animate="show"
          custom={0}
          className="font-mono-accent text-[11px] md:text-xs tracking-[0.35em] uppercase text-white/50 mb-6"
          data-testid="hero-eyebrow"
        >
          Northeast Ohio — Mobile Speed & Performance Training
        </motion.p>

        <motion.h1
          variants={rise}
          initial="hidden"
          animate="show"
          custom={1}
          className="font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl md:text-8xl lg:text-[9rem] max-w-5xl [word-spacing:0.05em]"
          data-testid="hero-title"
        >
          Speed Changes Everything.
        </motion.h1>

        <motion.p
          variants={rise}
          initial="hidden"
          animate="show"
          custom={2}
          className="mt-8 max-w-xl text-white/70 text-lg md:text-xl leading-relaxed"
          data-testid="hero-subtext"
        >
          Elite speed and performance training built for athletes who refuse to
          settle.
        </motion.p>

        <motion.p
          variants={rise}
          initial="hidden"
          animate="show"
          custom={2.5}
          className="mt-4 max-w-xl text-white/50 text-sm md:text-base leading-relaxed"
          data-testid="hero-global-msg"
        >
          Train in person across Northeast Ohio — or develop from anywhere through
          Origin Speed Academy.
        </motion.p>

        <motion.div
          variants={rise}
          initial="hidden"
          animate="show"
          custom={3}
          className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
        >
          <button
            data-testid="hero-cta-btn"
            onClick={() => scrollTo("#assessment")}
            className="group relative overflow-hidden bg-white text-black uppercase tracking-[0.2em] font-bold text-sm px-10 py-5 transition-transform duration-300 hover:scale-[1.02]"
          >
            <span className="relative z-10">Book Free Evaluation</span>
          </button>
          <button
            data-testid="hero-system-btn"
            onClick={() => scrollTo("#programs")}
            className="border border-white/30 text-white uppercase tracking-[0.2em] font-bold text-sm px-10 py-5 transition-colors duration-300 hover:bg-white hover:text-black"
          >
            Explore Programs
          </button>
        </motion.div>
      </div>

      {/* marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-black/40 backdrop-blur-sm overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex shrink-0">
              {Array.from({ length: 6 }).map((__, j) => (
                <span
                  key={j}
                  className="font-mono-accent text-[11px] tracking-[0.3em] uppercase text-white/40 mx-6"
                >
                  JJ SPEED FACTORY <span className="text-white/70">—</span> BUILT FOR SEPARATION
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <div className="absolute bottom-24 right-6 md:right-12 z-10 hidden md:block text-white/30">
        <ArrowDown className="animate-bounce" size={22} />
      </div>
    </section>
  );
}
